import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { FileImageIcon, InfoIcon, Trash2Icon } from "@lucide/icons";

import { useThrottle } from "simplydo/react/hooks";
import { css, styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { CardGroup } from "simplydo/react/ui/card";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H3, H4, H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Message } from "simplydo/react/ui/message";
import { Popup } from "simplydo/react/ui/popup";
import { Segment } from "simplydo/react/ui/segment";

import api from "api";
import util from "utils/utils";

import SuggestedIdeaCard from "components/lib/Cards/SuggestedIdeaCard";
import { ImageChooser } from "components/lib/Choosers";
import { HandledIdeaPreview } from "components/lib/Ideas/IdeaPreview";

import type {
  IdeaChallenge,
  IdeaOmniSearchResult,
  UpdateIdeaFn,
} from "../types";

const bodyContainerClass = css`
  border: none;
`;

const SuggestedHeader = styled.div`
  > h4,
  span {
    margin: 0;
    color: #363636;
  }
  margin-top: 20px;
`;

const StyledParagraph = styled.p`
  margin-top: 0px;
  margin-bottom: 15px;
  font-size: 13px;
  color: #2a2828;
  padding: 3px;
  padding-left: 6px;
  border-left: 4px solid #ffe4b5;
  background-color: #fffad1;
  display: block;
`;

const RemoveCoverImage = styled.div`
  display: flex;
  flex-direction: row;

  position: absolute;
  top: 5px;
  right: 5px;
`;

const ChangeCoverImage = styled.div``;

const IdeaCoverImage = styled.div<{ $imageUrl: string }>`
  position: relative;
  width: 100%;
  border-radius: 5px;
  margin-top: 10px;
  height: 250px;
  background-size: cover;
  background-image: ${({ $imageUrl }) =>
    $imageUrl ? `url(${$imageUrl})` : "none"};
  background-position: center;
  padding: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    box-shadow: inset 0 0 0 2000px rgba(104, 104, 104, 0.3);
  }
  ${ChangeCoverImage} {
    opacity: ${({ theme }) => (!theme.sizes.isMobile ? 0 : 1)};
  }
  &:hover {
    ${ChangeCoverImage} {
      opacity: 1;
    }
  }
`;

const NameInput = ({
  ideaName,
  updateName,
  isDefaultName,
  setIdeaNameFocused,
}: {
  ideaName: string;
  updateName: (value: string) => void;
  isDefaultName: boolean;
  setIdeaNameFocused: (focused: boolean) => void;
}) => {
  const [defName, setDefName] = useState(isDefaultName ? "" : ideaName);
  useEffect(() => {
    setDefName(isDefaultName ? "" : ideaName);
  }, [isDefaultName, ideaName]);
  const { t } = useTranslation();

  return (
    <Input
      size="large"
      placeholder={t("ideas.submit.naming.placeholder")}
      fluid
      value={defName}
      onChange={(e) => {
        setDefName(e.target.value);
        if (ideaName !== e.target.value) {
          updateName(e.target.value);
        }
      }}
      onFocus={() => setIdeaNameFocused(true)}
      onBlur={() => setIdeaNameFocused(false)}
    />
  );
};

const IdeaNameField = ({
  challenge,
  isEditingName,
  isEditingCover,
  ideaCoverImage,
  ideaId,
  ideaName: propIdeaName,
  updateIdea,
}: {
  challenge?: IdeaChallenge;
  isEditingName?: boolean;
  isEditingCover?: boolean;
  ideaCoverImage?: string;
  ideaId: string;
  ideaName?: string;
  updateIdea: UpdateIdeaFn;
}) => {
  const [searchResults, setSearchResults] = useState<IdeaOmniSearchResult[]>(
    [],
  );
  const [previewId, setPreviewId] = useState("");
  const [ideaNameFocused, setIdeaNameFocused] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const { t } = useTranslation();
  const coverImageEnabled = !challenge?.preventIdeaCoverImages;
  const filteredResults = searchResults.filter((r) => r._id !== ideaId);

  const isDefaultCoverImage =
    ideaCoverImage === "https://cdn.simplydo.co.uk/images/idea.png";

  const [defaultIdeaName, setDefaultIdeaName] = useState("");
  const isDefaultName = propIdeaName === defaultIdeaName;
  const ideaName = isDefaultName ? "" : (propIdeaName ?? "");

  useEffect(() => {
    setDefaultIdeaName(
      `${t("common:capitalise", { key: "generic.idea" })} for ${challenge?.name}`,
    );
  }, [challenge?.name, t]);

  const updateName = useCallback(
    (value: string) => {
      if (value.length === 0) {
        value = defaultIdeaName;
      }
      updateIdea(value, ["name"]);
    },
    [updateIdea, defaultIdeaName],
  );

  const throttledSearch = useThrottle((ideaId, term) => {
    if (!term) return;
    api.search.omni(
      { query: term, include: ["ideas"] },
      ({ ideas }) => {
        setSearchResults(ideas.filter((r) => r._id !== ideaId));
      },
      () => {},
    );
  }, 1000);

  const searchIdeas = useCallback(() => {
    throttledSearch(ideaId, ideaName);
  }, [ideaId, ideaName, throttledSearch]);

  useEffect(() => {
    if (isEditingName && !isDefaultName && (ideaName?.length ?? 0) > 2) {
      searchIdeas();
    } else {
      setSearchResults([]);
    }
  }, [isEditingName, ideaName, isDefaultName, searchIdeas]);

  const setCoverImage = useCallback(
    (filename: string | null, _name: string | null, url: string | null) => {
      updateIdea(filename, ["coverImage"]);
      if (!filename) {
        updateIdea([], ["coverImageThumbnails"]);
        updateIdea({}, ["coverImageThumbnailUrls"]);
      }
      updateIdea(url, ["coverImageUrl"], true);
    },
    [updateIdea],
  );

  useEffect(() => {
    if (filteredResults.length && ideaNameFocused) {
      setPopupOpen(true);
    }
    if (ideaNameFocused && !filteredResults.length) {
      setPopupOpen(false);
    }
  }, [filteredResults, ideaNameFocused]);

  const ChangeCoverImageButton = useMemo(
    () => (
      <div style={isDefaultCoverImage ? { marginTop: 10 } : {}}>
        <ImageChooser
          forType="idea"
          forId={ideaId}
          onComplete={setCoverImage}
          trigger={
            <Button fluid={isDefaultCoverImage} color="primary">
              <Icon icon={FileImageIcon} />
              <Popup
                content={t("ideas.coverimage.info")}
                trigger={
                  <p>
                    {isDefaultCoverImage
                      ? t("users.edit.coverImage.select")
                      : t("users.edit.coverImage.change")}
                  </p>
                }
                position="top center"
                wide
              />
            </Button>
          }
        />
      </div>
    ),
    [isDefaultCoverImage, setCoverImage, ideaId, t],
  );

  const showCoverEditor = isEditingCover && coverImageEnabled;
  if (!isEditingName && !showCoverEditor) return null;
  const sorted = [...filteredResults].sort(
    (a, b) => (b.completeness ?? 0) - (a.completeness ?? 0),
  );
  return (
    <Grid stackable>
      <GridColumn
        className={bodyContainerClass}
        style={{ borderRadius: 5, marginBottom: 25 }}
      >
        <HandledIdeaPreview
          ideaId={previewId}
          onCloseModal={() => setPreviewId("")}
        />
        <Segment
          style={{
            borderRadius: 5,
            boxShadow: "0px 5px 10px 0px rgba(0, 0, 0, 0.1)",
          }}
        >
          {isEditingName ? (
            <>
              <div style={{ marginBottom: 5 }}>
                <H3>{t("common:capitalise", { key: "generic.idea" })} name</H3>
              </div>
              <StyledParagraph>
                <Icon icon={InfoIcon} style={{ marginRight: 5 }} />
                {challenge?.ideaNameHelpText || t("ideas.name.auto.suggestion")}
              </StyledParagraph>
              <NameInput
                isDefaultName={isDefaultName}
                ideaName={ideaName ?? ""}
                updateName={updateName}
                setIdeaNameFocused={setIdeaNameFocused}
              />
              {filteredResults.length > 0 && popupOpen && (
                <Message>
                  <SuggestedHeader>
                    <H4>Related {t("generic.ideas")}</H4>
                    <span>
                      Here are some similar {t("generic.ideas")} we found based
                      on the name you entered...
                    </span>
                  </SuggestedHeader>
                  <CardGroup
                    style={{ marginBottom: 10, marginTop: 5 }}
                    itemsPerRow={3}
                    stackable
                  >
                    {sorted.slice(0, 3).map((result) => (
                      <SuggestedIdeaCard
                        onHeaderClicked={() => setPreviewId(result._id)}
                        key={result._id}
                        idea={result}
                      />
                    ))}
                  </CardGroup>
                </Message>
              )}
            </>
          ) : null}
          {showCoverEditor ? (
            <>
              <H5>{t("users.edit.coverImage.title")}</H5>
              {isDefaultCoverImage ? (
                ChangeCoverImageButton
              ) : (
                <IdeaCoverImage
                  $imageUrl={util.mixinCssUrlFallback(
                    ideaCoverImage ?? "",
                    util.ideaCoverImage(undefined),
                  )}
                >
                  {!isDefaultCoverImage ? (
                    <RemoveCoverImage>
                      {ChangeCoverImageButton}
                      <Button
                        onClick={() =>
                          util
                            .confirm(
                              "Remove cover image",
                              `Are you sure you want to remove the cover image for this ${t("generic.idea")}?`,
                            )
                            .then(() => setCoverImage(null, null, null))
                            .catch(() => {})
                        }
                      >
                        <Icon icon={Trash2Icon} />
                        Remove cover image
                      </Button>
                    </RemoveCoverImage>
                  ) : null}
                </IdeaCoverImage>
              )}
            </>
          ) : null}
        </Segment>
      </GridColumn>
    </Grid>
  );
};

export default IdeaNameField;
